import { MutableRefObject, useEffect, useRef, useState } from 'react';
import echarts, { EChartClickParams, EChartsCoreOption, EChartsInstance, getInitOptions } from './echartsCore';
import { getEchartsThemeName } from './echartsTheme';
import { useTheme } from '../../../context/ThemeContext';
import PlotLineOverlay from './PlotLineOverlay';
import { ChartPlotLine } from '../../../types/ChartPlotLine';

const EMPTY_PLOT_LINES: ChartPlotLine[] = [];

const MIN_LABEL_WIDTH = 80;
const BAR_MIN_CAP = 2;
const THIN_FACTORS = [1, 2, 3, 4, 6, 12];

const optionTicks = (option: EChartsCoreOption): number[] | null => {
    const axis: any = (option as any).xAxis;
    const values = axis && !Array.isArray(axis) ? axis.axisLabel?.customValues : undefined;
    return Array.isArray(values) && values.length > 1 ? values : null;
};


const applyTickDensity = (
    chart: EChartsInstance, ticks: number[] | null, applied: MutableRefObject<number | null>,
) => {
    if (!ticks) {
        applied.current = null;
        return;
    }
    const width = chart.getWidth();
    if (!width) {
        return;
    }
    const maxLabels = Math.max(2, Math.floor(width / MIN_LABEL_WIDTH));
    const factor = THIN_FACTORS.find(candidate => Math.ceil(ticks.length / candidate) <= maxLabels)
        ?? THIN_FACTORS[THIN_FACTORS.length - 1];
    if (factor === applied.current) {
        return;
    }
    applied.current = factor;
    const thinned = factor === 1 ? ticks : ticks.filter((_, index) => index % factor === 0);
    chart.setOption({
        xAxis: { axisLabel: { customValues: thinned }, axisTick: { customValues: thinned } },
    });
};

const DENSE_BAND_WIDTH = 9;
const WIDE_BAR_RATIO = 0.57;
const DENSE_BAR_RATIO = 0.38;

const measureBarWidth = (chart: EChartsInstance): number | null => {
    const model: any = (chart as any).getModel?.();
    if (!model || typeof model.eachSeries !== 'function') {
        return null;
    }
    let width: number | null = null;
    model.eachSeries((series: any) => {
        if (width !== null || series.subType !== 'bar') {
            return;
        }
        const layout = series.getData?.()?.getItemLayout?.(0);
        if (layout && typeof layout.width === 'number') {
            width = layout.width;
        }
    });
    return width;
};

const applyBarWidth = (
    chart: EChartsInstance, applied: MutableRefObject<number | null>, bandFraction?: number,
) => {
    const barWidth = measureBarWidth(chart);
    if (barWidth === null) {
        applied.current = null;
        return;
    }
    const band = barWidth / (applied.current ?? WIDE_BAR_RATIO);
    const ratio = band < DENSE_BAND_WIDTH ? DENSE_BAR_RATIO : WIDE_BAR_RATIO;
    if (ratio === applied.current) {
        return;
    }
    applied.current = ratio;
    const borderWidth = ratio === WIDE_BAR_RATIO ? 1 : 0;
    const cap = bandFraction && chart.getWidth()
        ? Math.max(BAR_MIN_CAP, chart.getWidth() * bandFraction * ratio)
        : undefined;
    const series: { type?: string }[] = (chart.getOption() as any).series ?? [];
    chart.setOption({
        series: series.map(item => (item.type === 'bar'
            ? {
                barWidth: `${Math.round(ratio * 100)}%`,
                itemStyle: { borderWidth },
                ...(cap !== undefined ? { barMaxWidth: cap } : {}),
            }
            : {})),
    });
};

const applyArrowCursor = (instance: EChartsInstance) => {
    const zr: any = instance.getZr();
    if (!zr || typeof zr.setCursorStyle !== 'function') {
        return;
    }
    const setCursorStyle = zr.setCursorStyle.bind(zr);
    zr.setCursorStyle = (cursor: string) => setCursorStyle(cursor === 'crosshair' ? 'default' : cursor);
};

export interface EChartProps {
    barBandFraction?: number;
    // Keep the option's own bar widths rather than sizing bars to the band.
    fixedBarWidth?: boolean;
    option: EChartsCoreOption;
    height?: number;
    className?: string;
    plotLines?: ChartPlotLine[];
    onInit?: (chart: EChartsInstance) => void;
    onClick?: (params: EChartClickParams) => void;
}

function EChart(props: EChartProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const onInitRef = useRef(props.onInit);
    onInitRef.current = props.onInit;
    const onClickRef = useRef(props.onClick);
    onClickRef.current = props.onClick;

    const [chart, setChart] = useState<EChartsInstance | null>(null);
    const barRatioRef = useRef<number | null>(null);
    const tickFactorRef = useRef<number | null>(null);
    const baseTicks = optionTicks(props.option);
    const { theme } = useTheme();
    const height = props.height ?? 200;
    const plotLines = props.plotLines ?? EMPTY_PLOT_LINES;

    useEffect(() => {
        const element = containerRef.current;
        if (!element) {
            return;
        }
        const instance = echarts.init(element, getEchartsThemeName(theme), getInitOptions());
        applyArrowCursor(instance);
        instance.on('click', (params: EChartClickParams) => {
            if (onClickRef.current) {
                onClickRef.current(params);
            }
        });
        setChart(instance);
        if (onInitRef.current) {
            onInitRef.current(instance);
        }
        return () => {
            instance.dispose();
            setChart(null);
        };
    }, [theme]);

    useEffect(() => {
        if (!chart || chart.isDisposed()) {
            return;
        }
        barRatioRef.current = null;
        tickFactorRef.current = null;
        chart.setOption(props.option, { notMerge: true });
        applyTickDensity(chart, baseTicks, tickFactorRef);
        if (!props.fixedBarWidth) {
            applyBarWidth(chart, barRatioRef, props.barBandFraction);
        }
    }, [chart, props.option, baseTicks, props.barBandFraction, props.fixedBarWidth]);

    useEffect(() => {
        const element = containerRef.current;
        if (!chart || !element || typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(() => {
            if (!chart.isDisposed()) {
                chart.resize();
                applyTickDensity(chart, baseTicks, tickFactorRef);
                if (!props.fixedBarWidth) {
                    applyBarWidth(chart, barRatioRef, props.barBandFraction);
                }
            }
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, [chart, baseTicks, props.barBandFraction, props.fixedBarWidth]);

    return (
        <div style={{ position: 'relative' }} className={props.className}>
            <div ref={containerRef} data-testid="echart" className="chart-container"
                 style={{ width: '100%', height: `${height}px` }}/>
            <PlotLineOverlay chart={chart} plotLines={plotLines}/>
        </div>
    );
}

export default EChart;
